/**
 * OTP Digit Field Controller
 *
 * Drives the split six-box entry for a multi-factor code, assembling the digits into a single
 * hidden field that the server reads.
 *
 * Accessibility rules this controller must not break (see CU-86cb3pp61):
 *
 * - **Never `preventDefault()` a navigation key.** An earlier version allow-listed the keys it
 *   accepted on `keydown` and suppressed everything else, which included Tab and Shift+Tab —
 *   so keyboard focus could not leave the group at all and the Continue button was unreachable.
 *   That is a WCAG 2.1.2 keyboard trap on a screen nobody can route around. Unwanted characters
 *   are stripped on `input` instead, which cannot trap focus.
 * - **Paste must work.** A pasted or autofilled code is distributed across the boxes; without
 *   this, `maxlength="1"` means only the first character ever lands (WCAG 3.3.8).
 * - Labelling lives in the view: a `role="group"` with an accessible name around the set, and a
 *   per-box `aria-label`. Do not remove them.
 */
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['mainField', 'digit']
  static values = { codeLength: String }

  connect() {
    this.#updateMainField()
  }

  get digits() {
    // Prefer targets; fall back to a DOM query so older markup keeps working.
    return this.hasDigitTarget
      ? this.digitTargets
      : Array.from(this.element.querySelectorAll('.otp-digit-fields-container input'))
  }

  get length() {
    return Number(this.codeLengthValue) || this.digits.length
  }

  // ---------------------------------------------------------------- input

  /**
   * Strip anything that is not a digit, then advance. Filtering here rather than on `keydown`
   * means every navigation and editing key keeps its native behaviour.
   */
  handleInputEvent(event) {
    const field = event.currentTarget
    const cleaned = field.value.replace(/\D/g, '')

    // More than one character can arrive at once — autofill, IME, or a paste the browser
    // handled itself. Spread it rather than throwing the rest away.
    if (cleaned.length > 1) {
      this.#distribute(cleaned, this.digits.indexOf(field))
      return
    }

    field.value = cleaned
    if (cleaned) this.#focus(this.digits.indexOf(field) + 1)
    this.#updateMainField()
  }

  /** Distribute a pasted code across the boxes, starting at the focused one. */
  handlePaste(event) {
    const text = (event.clipboardData || window.clipboardData)?.getData('text') || ''
    const cleaned = text.replace(/\D/g, '')
    if (!cleaned) return

    event.preventDefault() // safe: paste is not a focus-navigation key
    this.#distribute(cleaned, this.digits.indexOf(event.currentTarget))
  }

  /**
   * Backspace on an empty box steps back and clears the previous one, which is what users
   * expect from split code inputs. Everything else is left to the browser.
   */
  handleKeyDown(event) {
    if (event.key !== 'Backspace') return

    const field = event.currentTarget
    if (field.value) return

    const previous = this.digits[this.digits.indexOf(field) - 1]
    if (!previous) return

    event.preventDefault()
    previous.value = ''
    previous.focus()
    this.#updateMainField()
  }

  // ---------------------------------------------------------------- private

  #distribute(code, startIndex) {
    const fields = this.digits
    const from = startIndex < 0 ? 0 : startIndex
    const chars = code.slice(0, this.length - from).split('')

    chars.forEach((char, offset) => {
      const field = fields[from + offset]
      if (field) field.value = char
    })

    this.#updateMainField()

    // Land on the first empty box, or the submit button once the code is complete.
    const nextEmpty = fields.findIndex((f) => !f.value)
    if (nextEmpty === -1) {
      this.#focusSubmit()
    } else {
      fields[nextEmpty].focus()
    }
  }

  #focus(index) {
    const field = this.digits[index]
    if (field) field.focus()
  }

  #focusSubmit() {
    const form = this.mainFieldTarget.form
    const submit = form?.querySelector('[type="submit"], button:not([type="button"])')
    if (submit) submit.focus()
  }

  #updateMainField() {
    this.mainFieldTarget.value = this.digits.map((field) => field.value).join('')
  }
}
